import {customThemeConstants} from '@hconnect/uikit/src/lib2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataset
} from 'chart.js'
import moment from 'moment'
import {useMemo} from 'react'
import {Bar} from 'react-chartjs-2'
import {useTranslation} from 'react-i18next'

import {DOWNTIME_ANALYTICS_WIDGET_NAMESPACE} from '../consts'
import {useDowntimeBarChartOptions} from '../hooks'
import {StoppageCategoryCounts} from '../types'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

type LabelVariant = 'firstLetter' | 'threeLetters' | 'default'

type Props = {
  data: StoppageCategoryCounts[]
  abbreviatedLegendLabels?: boolean
  labelVariant?: LabelVariant
}

const DATASET_INDEX = {
  ELECTRICAL: 0,
  MECHANICAL: 1,
  PROCESS: 2,
  OTHER: 3
} as const

const TranslationKeysMap = {
  [DATASET_INDEX.ELECTRICAL]: 'downtimeByCategoryBarChart.label.electrical',
  [DATASET_INDEX.MECHANICAL]: 'downtimeByCategoryBarChart.label.mechanical',
  [DATASET_INDEX.PROCESS]: 'downtimeByCategoryBarChart.label.process',
  [DATASET_INDEX.OTHER]: 'downtimeByCategoryBarChart.label.other'
} as const

const getLegendTranslationKey = (index: number, abbreviatedLabels?: boolean) => {
  const translationKey = TranslationKeysMap[index]

  return abbreviatedLabels ? `${translationKey}Abbreviated` : translationKey
}

const formatLabel = (month: number, year: number, language: string, labelVariant: LabelVariant) => {
  const date = moment({month: month - 1, year}).locale(language)

  switch (labelVariant) {
    case 'firstLetter':
      return date.format('MMM').charAt(0).toUpperCase()
    case 'threeLetters':
      return date.format('MMM')
    default:
      return date.format('MMM YYYY')
  }
}

export const DowntimeByCategoryBarChart = ({
  data,
  abbreviatedLegendLabels: abbreviatedLabels,
  labelVariant = 'default'
}: Props) => {
  const {t, i18n} = useTranslation(DOWNTIME_ANALYTICS_WIDGET_NAMESPACE)
  const language = i18n.language
  const chartOptions = useDowntimeBarChartOptions()

  const chartData = useMemo(() => {
    const datasets: ChartDataset<'bar'>[] = [
      {
        label: t(getLegendTranslationKey(DATASET_INDEX.ELECTRICAL, abbreviatedLabels)),
        data: [],
        backgroundColor: customThemeConstants().palette.accent1,
        barThickness: 'flex'
      },
      {
        label: t(getLegendTranslationKey(DATASET_INDEX.MECHANICAL, abbreviatedLabels)),
        data: [],
        backgroundColor: customThemeConstants().palette.accent2,
        barThickness: 'flex'
      },
      {
        label: t(getLegendTranslationKey(DATASET_INDEX.PROCESS, abbreviatedLabels)),
        data: [],
        backgroundColor: customThemeConstants().palette.accent5,
        barThickness: 'flex'
      },
      {
        label: t(getLegendTranslationKey(DATASET_INDEX.OTHER, abbreviatedLabels)),
        data: [],
        backgroundColor: customThemeConstants().palette.barChartDefault,
        barThickness: 'flex'
      }
    ]

    const labels: string[] = []
    data.forEach((stoppageCategoryCount) => {
      labels.push(
        formatLabel(stoppageCategoryCount.month, stoppageCategoryCount.year, language, labelVariant)
      )
      datasets[DATASET_INDEX.ELECTRICAL].data.push(stoppageCategoryCount.electricalCount)
      datasets[DATASET_INDEX.MECHANICAL].data.push(stoppageCategoryCount.mechanicalCount)
      datasets[DATASET_INDEX.PROCESS].data.push(stoppageCategoryCount.processCount)
      datasets[DATASET_INDEX.OTHER].data.push(stoppageCategoryCount.otherCount)
    })

    return {
      labels,
      datasets
    }
  }, [data, abbreviatedLabels, t, language, labelVariant])

  return (
    <Bar options={chartOptions} data={chartData} data-test-id="stoppages-category-count-chart" />
  )
}
